/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import ProductScroller from '../product-scroller'
import useEinstein from '../../hooks/use-einstein'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useIntersectionObserver from '@salesforce/retail-react-app/app/hooks/use-intersection-observer'
import {useWishList} from '../../hooks/use-wish-list'
import {useGlobaleContext} from '../../contexts/globale'
import {NoSearchSuggestions} from '../search/partials/no-search-suggestions'

// Recommender description to be used as a heading for the recommendations
const getRecommenderHeading = (recommendersResponse, recommenderName) => {
    if (!recommenderName || !recommendersResponse) {
        return ''
    }

    const recommenders = Array.isArray(recommendersResponse)
        ? recommendersResponse
        : recommendersResponse?.recommenders || recommendersResponse?.data || []

    const matchedRecommender = recommenders.find((item) => {
        return item?.name === recommenderName
    })

    return matchedRecommender?.description || matchedRecommender?.name || ''
}

/**
 * A component for fetching and rendering product recommendations from the Einstein API
 * by a zone or a recommender name.
 */
const RecommendedProducts = ({
    zone,
    recommender,
    products,
    dataLayerLoaded = true,
    isEmptySearch,
    closeAndNavigate,
    searchQuery,
    quickAddLabel = true,
    title,
    titleFontFamily,
    itemWidth = {base: '175px', lg: '300px'},
    pageData = null,
    titleColor = 'black',
    recentSearches = null,
    titleTag = 'h2',
    shouldFetch,
    analyticsProps = {},
    openQuickAddModal,
    ...props
}) => {
    const {
        isLoading,
        recommendations,
        getZoneRecommendations,
        getRecommendations,
        getRecommenders,
        sendClickReco,
        sendViewReco
    } = useEinstein()
    const {data: customer} = useCurrentCustomer()
    const {customerId} = customer
    const {countryISO, currencyCode} = useGlobaleContext()
    const {data: wishlist} = useWishList()

    const ref = useRef()
    const isOnScreen = useIntersectionObserver(ref, {useOnce: true})
    const [_products, setProducts] = useState(products)
    const [recommenderHeading, setRecommenderHeading] = useState('')
    // Polling function to make sure customerId & listId are available
    const customerIdRef = useRef(null)
    const listIdRef = useRef(null)

    useEffect(() => {
        if (customerIdRef) {
            customerIdRef.current = customerId
        }
    }, [customerId])
    useEffect(() => {
        if (listIdRef) {
            listIdRef.current = wishlist?.id || null
        }
    }, [wishlist])

    useEffect(() => {
        // Check if the component should fetch results or not. This is useful
        // when you are still waiting on additional data, like `products`.
        if (typeof shouldFetch === 'function' && !shouldFetch()) {
            return
        }
        if (!countryISO || !currencyCode) {
            return
        }
        // Fetch either zone or recommender, but not both. If a zone and recommender
        // name are both provided, `zone` takes precendence.
        if (zone) {
            getZoneRecommendations(zone, _products)
            return
        }
        if (recommender) {
            const categories = pageData?.category
                ? [
                      {
                          id: pageData.category
                      }
                  ]
                : null
            if (categories) {
                getRecommendations(recommender, _products, {categories: categories})
            } else {
                getRecommendations(recommender, _products)
            }
            return
        }
    }, [zone, recommender, _products, countryISO, currencyCode])

    useEffect(() => {
        // This is an optimization that eliminates superfluous rerenders/fetching by
        // keeping a copy of the `products` array prop in state for shallow comparison.
        if (!Array.isArray(products)) {
            return
        }
        if (
            products.length !== _products?.length ||
            !products.every((val, index) => val === _products?.[index])
        ) {
            setProducts(products)
        }
    }, [products, countryISO])

    useEffect(() => {
        let isMounted = true

        const loadRecommenderHeading = async () => {
            if (!recommender) {
                if (isMounted) {
                    setRecommenderHeading('')
                }
                return
            }

            try {
                const recommenderResponse = await getRecommenders()
                if (!isMounted) {
                    return
                }

                setRecommenderHeading(getRecommenderHeading(recommenderResponse, recommender))
            } catch {
                if (isMounted) {
                    setRecommenderHeading('')
                }
            }
        }

        loadRecommenderHeading()

        return () => {
            isMounted = false
        }
    }, [getRecommenders, recommender])

    useEffect(() => {
        if ((isEmptySearch || isOnScreen) && recommendations?.recs) {
            sendViewReco(
                {
                    recommenderName: recommendations.recommenderName,
                    __recoUUID: recommendations.recoUUID
                },
                recommendations.recs.map((rec) => ({id: rec.id}))
            )
        }
    }, [isOnScreen, recommendations, isEmptySearch])

    // The component should remove itself altogether if it has no recommendations
    // and we aren't loading any.
    if (!isLoading && (!recommendations || !recommendations?.recs?.length)) {
        return null
    }

    const recommendationTitle =
        title || recommendations?.displayMessage?.trim() || recommenderHeading?.trim()

    return isEmptySearch ? (
        <NoSearchSuggestions
            isLoading={isLoading}
            products={recommendations.recs}
            closeAndNavigate={closeAndNavigate}
            recentSearches={recentSearches}
            searchQuery={searchQuery}
        />
    ) : (
        <ProductScroller
            ref={ref}
            titleFontFamily={titleFontFamily}
            title={recommendationTitle}
            titleColor={titleColor}
            titleTag={titleTag}
            type="recommender-products"
            enableFavourite={true}
            dataLayerLoaded={dataLayerLoaded}
            showImageSlider={true}
            quickAddLabel={quickAddLabel}
            products={recommendations.recs}
            itemWidth={itemWidth}
            isLoading={isLoading}
            openQuickAddModal={openQuickAddModal}
            productTileProps={(product) => ({
                onClick: () => {
                    sendClickReco(
                        {
                            recommenderName: recommendations.recommenderName,
                            __recoUUID: recommendations.recoUUID
                        },
                        product
                    )
                },
                enableFavourite: true
            })}
            {...props}
            analyticsProps={analyticsProps}
        />
    )
}

RecommendedProducts.propTypes = {
    /* The title to appear above the product scroller */
    title: PropTypes.any,
    titleColor: PropTypes.string,
    titleFontFamily: PropTypes.string,
    pageData: PropTypes.any,
    recentSearches: PropTypes.any,
    quickAddLabel: PropTypes.bool,
    dataLayerLoaded: PropTypes.bool,
    itemWidth: PropTypes.any,
    titleTag: PropTypes.string,

    /* The zone to request */
    zone: PropTypes.string,

    /* The recommender to request */
    recommender: PropTypes.string,

    /* The products to use for recommendation context */
    products: PropTypes.arrayOf(PropTypes.object),

    /* Callback to determine if the component should fetch results */
    shouldFetch: PropTypes.func,

    /* This will be true if recommender is an empty search one */
    isEmptySearch: PropTypes.bool,

    /* This will have the search query for no search recommenders */
    searchQuery: PropTypes.string,

    closeAndNavigate: PropTypes.func,
    // slider settings
    sliderGroup: PropTypes.string,
    analyticsProps: PropTypes.object,
    openQuickAddModal: PropTypes.bool
}

export default RecommendedProducts
